// src/contexts/registration/provider.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { RegistrationContext } from './context';
import {
  RegistrationState,
  BusinessRegistrationData,
  StaffRegistrationData,
  LoadingStates
} from '../../types/registration';

interface RegistrationProviderProps {
  children: React.ReactNode;
}

const initialState: RegistrationState = {
  type: 'business',
  currentStep: 1,
  completedSteps: [],
  stepsData: {},
  isCompleted: false
};

const initialLoadingStates: LoadingStates = {
  checkingRegistration: false,
  updatingStep: false,
  completing: false
};

export function RegistrationProvider({ children }: RegistrationProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<RegistrationState>(initialState);
  const [loadingStates, setLoadingStates] = useState<LoadingStates>(initialLoadingStates);
  const [draftData, setDraftData] = useState<Partial<RegistrationState['stepsData']>>({});
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);

  // טען טיוטה מלוקאל
  useEffect(() => {
    const draft = localStorage.getItem('registration_draft');
    if (draft) {
      const parsedDraft = JSON.parse(draft);
      setDraftData(parsedDraft);
      setState(prev => ({
        ...prev,
        stepsData: { ...prev.stepsData, ...parsedDraft }
      }));
    }
  }, []);

  // סנכרון שלב לפי URL
  useEffect(() => {
    if (isRedirecting) return;

    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam) {
      const stepNum = Number(stepParam);
      if (stepNum >= 1 && stepNum <= 6 && state.currentStep !== stepNum) {
        setState(prev => ({ ...prev, currentStep: stepNum }));
      }
    }
 }, [location.search, isRedirecting]); 

  const saveDraft = (step: number, data: any) => {
    const newDraft = { ...draftData, [`step${step}`]: data };
    setDraftData(newDraft);
    localStorage.setItem('registration_draft', JSON.stringify(newDraft));
  };

  const updateStep = async (step: number, data: any) => {
    if (step < 1 || step > 5) return;

    setLoadingStates(prev => ({ ...prev, updatingStep: true }));

    try {
      setState(prev => ({
        ...prev,
        currentStep: step >= 5 ? 5 : step + 1,
        completedSteps: [...new Set([...prev.completedSteps, step])],
        stepsData: {
          ...prev.stepsData,
          [`step${step}`]: data
        }
      }));

      saveDraft(step, data);
    } catch (error: any) {
      toast.error(error.message || 'שגיאה בשמירת השלב');
      throw error;
    } finally {
      setLoadingStates(prev => ({ ...prev, updatingStep: false }));
    }
  };

  const submitFullRegistration = async () => {
    setLoadingStates(prev => ({ ...prev, completing: true }));

    try {
      const step1 = state.stepsData.step1 as BusinessRegistrationData['step1'];
      const step2 = state.stepsData.step2 as BusinessRegistrationData['step2'];
      const step3 = state.stepsData.step3 as BusinessRegistrationData['step3'];
      const step4 = state.stepsData.step4 as BusinessRegistrationData['step4'] | undefined;

      // if (!step1?.email || !step1?.password || !step1?.name || !step1?.phone) {
      //   throw new Error('פרטי התחברות חסרים');
      // }
      if (!step2?.name || !step2?.type) {
        throw new Error('פרטי עסק חסרים');
      }
      if (!step3?.hours) {
        throw new Error('שעות פעילות חסרות');
      }

      // 1) יצירת משתמש (Auth)
      console.log('🟢 [RegistrationProvider] ===== STARTING FINAL REGISTRATION =====');
      console.log('🟢 [RegistrationProvider] Step1 name:', step1.name);
      console.log('🟢 [RegistrationProvider] Step1 email:', step1.email);
      console.log('🟢 [RegistrationProvider] Step1 full data:', step1);

      const { error: signUpError } = await supabase.auth.signUp({
        email: step1.email,
        password: step1.password,
        options: {
          data: {
            name: step1.name,
            full_name: step1.name,
            phone: step1.phone,
            role: 'admin',
          }
        }
      });
      if (signUpError) throw signUpError;

      console.log('🟢 [RegistrationProvider] Auth user created successfully');

      // 2) התחברות
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: step1.email,
        password: step1.password
      });
      if (loginError) throw loginError;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('המשתמש לא נוצר בהצלחה');

      console.log('🟢 [RegistrationProvider] User logged in, user_metadata:', user.user_metadata);
      console.log('🟢 [RegistrationProvider] User ID:', user.id);

      // ✅ 2.1 הבטחת רשומה בטבלת public.users (במקרים שאין טריגר/יש דילי)
      {
        console.log('🟢 [RegistrationProvider] Upserting to public.users with name:', step1.name);

        const { error: ensureErr } = await supabase
          .from('users')
          .upsert(
            {
              id: user.id,
              name: step1.name,
              email: step1.email,
              phone: step1.phone,
              role: 'admin',
              completed: 0,
              metadata: {
                full_name: step1.name
              }
            },
            { onConflict: 'id' }
          );
        if (ensureErr) {
          console.error('🔴 [RegistrationProvider] Error ensuring user exists:', ensureErr);
        } else {
          console.log('🟢 [RegistrationProvider] User upserted successfully');
        }
      }

      // 3) קישור העסק - business_id צריך כבר להיות קיים מהשלב 2
      let businessId = localStorage.getItem('businessId');

      console.log('[RegistrationProvider] Starting final registration with business_id:', businessId);

      if (!businessId) {
        // Fallback: אם משום מה business_id לא קיים, ניצור עסק עכשיו
        console.warn('[RegistrationProvider] No business_id found, creating business now (fallback)');

        const { data: bookingLinkData, error: bookingError } = await supabase.rpc('generate_booking_link', {
          name: step2.name
        });
        if (bookingError || !bookingLinkData) {
          console.error('[RegistrationProvider] Error generating booking link:', bookingError);
          throw new Error('שגיאה ביצירת קישור הזמנה');
        }
        const bookingLink = bookingLinkData;

        const fallbackBusinessData = {
          owner_id: user.id,
          name: step2.name,
          description: step2.description || '',
          address: step2.address || '',
          settings: { theme: 'light', notifications: true },
          type: 'licensed',
          business_category: step2.type,
          booking_link: bookingLink,
          logo_url: step2.logo_url || null
        };

        console.log('[RegistrationProvider] Creating business (fallback) with data:', {
          owner_id: fallbackBusinessData.owner_id,
          name: fallbackBusinessData.name,
          type: fallbackBusinessData.type,
          booking_link: fallbackBusinessData.booking_link
        });

        const { data: bizData, error: bizError } = await supabase
          .from('businesses')
          .insert(fallbackBusinessData)
          .select()
          .single();

        if (bizError) {
          console.error('[RegistrationProvider] Detailed error creating business (fallback):');
          console.error('  - Error message:', bizError.message);
          console.error('  - Error code:', bizError.code);
          console.error('  - Error details:', bizError.details);
          console.error('  - Error hint:', bizError.hint);
          console.error('  - Full error object:', JSON.stringify(bizError, null, 2));
          throw new Error(`שגיאה ביצירת העסק: ${bizError.message || 'שגיאה לא ידועה'}`);
        }

        if (!bizData?.id) {
          console.error('[RegistrationProvider] Business created but no ID returned:', bizData);
          throw new Error('שגיאה ביצירת העסק: לא התקבל מזהה עסק');
        }

        businessId = bizData.id;
        localStorage.setItem('businessId', businessId);
        console.log('[RegistrationProvider] Business created (fallback) with ID:', businessId);
      } else {
        // העסק כבר קיים - רק נוודא שהכל מעודכן
        console.log('[RegistrationProvider] Business already exists, no need to create');
      }

      // 4) עדכון טבלת users עם business_id (+ role=admin ב-DB)
      {
        const { data: updatedRows, error: updateUserError } = await supabase
          .from('users')
          .update({ business_id: businessId, role: 'admin' })
          .eq('id', user.id)
          .select('id'); // מחזיר שורה אם אכן עודכן

        if (updateUserError) throw updateUserError;
      }

      // המתנה קלה לסנכרון
      await new Promise(res => setTimeout(res, 1000));

      // 5) הכנסת/עדכון שעות פעילות
      const { data: existingHours } = await supabase
        .from('business_hours')
        .select('id')
        .eq('business_id', businessId)
        .maybeSingle();

      if (existingHours) {
        const { error: hoursUpdateError } = await supabase
          .from('business_hours')
          .update({
            regular_hours: step3.hours,
            special_dates: []
          })
          .eq('business_id', businessId);

        if (hoursUpdateError) throw hoursUpdateError;
      } else {
        const { error: hoursError } = await supabase
          .from('business_hours')
          .insert({
            business_id: businessId,
            regular_hours: step3.hours,
            special_dates: []
          });

        if (hoursError) throw hoursError;
      }

      // 6) שירותים (אם יש)
      if (step4?.services?.length) {
        // מחק שירותים קיימים קודם (למנוע כפילויות)
        await supabase
          .from('services')
          .delete()
          .eq('business_id', businessId);

        const services = step4.services.map(service => ({
          business_id: businessId,
          name: service.name_he,
          name_he: service.name_he,
          price: parseFloat(service.price || '0'),
          duration: `${service.duration || '30'} minutes`,
          description: service.description || ''
        }));
        const { error: servicesError } = await supabase.from('services').insert(services);

        if (servicesError) {
          console.error('Error inserting services:', servicesError);
          throw servicesError;
        }
      }

      // 7) מנוי – insert רק אם אין מנוי (לא לדרוס מנויים קיימים!)
      try {
        // בדיקה אם כבר קיים מנוי לעסק הזה
        const { data: existingSub } = await supabase
          .from('subscriptions')
          .select('id, stripe_subscription_id, stripe_customer_id')
          .eq('business_id', businessId)
          .maybeSingle();

        // אם יש מנוי מסטריפ - לא נוגעים בו!
        if (existingSub?.stripe_subscription_id || existingSub?.stripe_customer_id) {

        } else if (!existingSub) {
          // רק אם אין מנוי בכלל - ניצור חדש
          const selectedPlanKey = (localStorage.getItem('selectedPlan') || 'basic').trim();
          const selectedPlanCycle = (localStorage.getItem('selectedPlanCycle') || 'monthly').trim() as 'monthly'|'yearly';

          const { data: planRow, error: planErr } = await supabase
            .from('subscription_plans')
            .select('id, plan_key, code, name')
            .or(`plan_key.eq.${selectedPlanKey},code.eq.${selectedPlanKey},name.eq.${selectedPlanKey}`)
            .maybeSingle();

          if (!planRow?.id || planErr) {

          } else {
            const now = new Date();
            const periodDays = selectedPlanCycle === 'yearly' ? 365 : 14;
            const payload = {
              business_id: businessId,
              user_id: user.id,
              plan_id: planRow.id,
              status: 'trialing',
              billing_cycle: selectedPlanCycle,
              current_period_start: now.toISOString(),
              current_period_end: new Date(now.getTime() + periodDays * 24 * 60 * 60 * 1000).toISOString(),
              cancel_at_period_end: false,
            };

            const { data: subData, error: subErr } = await supabase
              .from('subscriptions')
              .insert(payload)
              .select()
              .single();

            if (subErr) {

            } else {

            }
          }
        }
      } catch (subCatch) {

      }

      // 8) סימון השלמה (DB + לוקאל) — עכשיו מובטח שקיימת שורה ב-public.users
      {
        const { error: completedErr } = await supabase
          .from('users')
          .update({ completed: 1, completed_at: new Date().toISOString() })
          .eq('id', user.id);
        if (completedErr) {

        }
        localStorage.setItem('completed', '1');
      }

      // 9) ניקוי בחירות – שלא ירוץ שוב בעתיד
      // localStorage.removeItem('selectedPlan');
      // localStorage.removeItem('selectedPlanCycle');
      // localStorage.removeItem('selectedPlanBusinessName');

      toast.success('ההרשמה הושלמה בהצלחה!');
      cleanup();

      navigate('/dashboard');
      window.location.reload();
    } catch (err: any) {

      toast.error(err.message || 'שגיאה בהרשמה');
    } finally {
      setLoadingStates(prev => ({ ...prev, completing: false }));
    }
  };

  const goToStep = (step: number) => {
    if (step < 1 || step > 5) return;
    if (!state.completedSteps.includes(step - 1) && step !== 1) return;
    setState(prev => ({ ...prev, currentStep: step }));
  };

  const goToPreviousStep = () => {
    if (state.currentStep > 1) {
      // אזהרה אם חוזרים משלב 2 ומעלה
      if (state.currentStep >= 2) {
        setShowBackWarningModal(true);
      } else {
        setState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
        setIsRedirecting(true);
        navigate(`/register?step=${state.currentStep - 1}`, { replace: true });
        setTimeout(() => setIsRedirecting(false), 100);
      }
    }
  };

  const confirmGoBack = () => {
    setShowBackWarningModal(false);
    setState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    setIsRedirecting(true);
    navigate(`/register?step=${state.currentStep - 1}`, { replace: true });
    setTimeout(() => setIsRedirecting(false), 100);
  };

  const isStepValid = (step: number): boolean => {
    const data = state.stepsData[`step${step}`] || draftData[`step${step}`];
    if (!data) return false;

    switch (state.type) {
      case 'business':
        switch (step) {
          case 1: return !!(data.email && data.password && data.name && data.phone);
          case 2: return !!(data.name && data.type);
          case 3: return !!data.hours;
          case 4: return true;
          default: return false;
        }
      case 'staff':
        switch (step) {
          case 1: return !!(data.email && data.password);
          case 2: return !!(data.name && data.phone);
          default: return false;
        }
    }
  };

  const getStepData = (step: number) => {
    return state.stepsData[`step${step}`] || draftData[`step${step}`] || null;
  };

  const cleanup = () => {
    setState(initialState);
    setDraftData({});
    localStorage.removeItem('registration_draft');
  };

  return (
    <RegistrationContext.Provider value={{
      state,
      loadingStates,
      draftData,
      isRedirecting,
      setIsRedirecting,
      updateStep,
      submitFullRegistration,
      completeRegistration: submitFullRegistration,
      goToStep,
      goToPreviousStep,
      isStepValid,
      getStepData,
      cleanup,
      showBackWarningModal,
      setShowBackWarningModal,
      confirmGoBack
    }}>
      {children}
    </RegistrationContext.Provider>
  );
}
